import moment from 'moment'
import { EnumClasshourStatus } from '@xt/client/enums'
import { AjaxBasics } from '@xt/client/helpers'
import type { ClasshourItem } from '@xt/client/entities/course-v2/types.d'
import type { ClasshourUIStatus, FormatedBiShanInfo, HomeworkBiShan, Unit } from '../types'

/**
 * 获取当前时间和目标时间之间的差异 各个单位数值独立
 *
 * 1年 ｜ 365天
 */
const getTimeDiffDays = (target: number) => {
  const now = AjaxBasics.serviceDate
  const days = moment(target).diff(now, 'day')
  const hours = moment(target).diff(now, 'hour')
  const minutes = moment(target).diff(now, 'minute')
  const seconds = moment(target).diff(now, 'second')

  return {
    days,
    hours,
    minutes,
    seconds
  }
}

/**
 * 获取当前时间和目标时间之间的差异 各个单位数值独立 (差异到小时)
 */
const getTimeDiffHours = (target: number) => {
  const now = AjaxBasics.serviceDate
  const hours = moment(target).diff(now, 'hour')
  const minutes = moment(target).diff(now, 'minute')
  const seconds = moment(target).diff(now, 'second')

  return {
    days: 0,
    hours,
    minutes,
    seconds
  }
}

function getTimeAndUnit(times: ReturnType<typeof getTimeDiffDays>): { time: number; unit: Unit } {
  const { days, hours, minutes } = times

  if (days > 0) {
    return {
      time: days,
      unit: '天'
    }
  }

  if (hours > 0) {
    return {
      time: hours,
      unit: '小时'
    }
  }

  return {
    time: Math.max(minutes, 1),
    unit: '分钟'
  }
}

export function formatClasshourUIStatus(classhour: ClasshourItem): ClasshourUIStatus {
  const now = AjaxBasics.serviceDate

  // 是否已经发布
  const isPublish = classhour.publishStatus === 1

  if (isPublish) {
    // 回顾剩余最高展示到小时
    if (classhour.studyBuffEndTime && now.isBefore(classhour.studyBuffEndTime)) {
      return {
        message: '回顾剩余',
        type: EnumClasshourStatus.StudyBuff,
        ...getTimeAndUnit(getTimeDiffHours(classhour.studyBuffEndTime))
      }
    }

    const isLiveEnd = classhour.liveEnd
    if (!isLiveEnd && isPublish && now.isAfter(classhour.startTime)) {
      return {
        message: '直播中',
        type: EnumClasshourStatus.Living
      }
    }

    if (classhour.playbackEndTime && now.isAfter(classhour.playbackEndTime)) {
      return {
        message: '结束啦',
        type: EnumClasshourStatus.PlaybackEnd
      }
    }

    // 课程正在回放中
    const isPlayback = classhour.playbackStatus === 1
    if (isLiveEnd) {
      if (isPlayback && classhour.playbackEndTime) {
        const times = getTimeDiffDays(classhour.playbackEndTime)

        if (times.days > 0 || times.hours > 0 || times.minutes > 0 || times.seconds > 0) {
          return {
            message: '回放剩余',
            type: EnumClasshourStatus.Playback,
            ...getTimeAndUnit(times)
          }
        }
      }

      return { message: '直播结束', type: EnumClasshourStatus.LiveEnd }
    }

    // 当天课程
    return {
      message: '距开课',
      type: EnumClasshourStatus.WillLive,
      ...getTimeAndUnit(getTimeDiffDays(classhour.startTime))
    }
  } else {
    return {
      type: EnumClasshourStatus.Unpublish,
      dateString: moment(classhour.startTime).utcOffset(+8).format('MM月DD日')
    }
  }
}

export function formatBiShan(classhour: ClasshourItem): Array<FormatedBiShanInfo> {
  const now = AjaxBasics.serviceDate
  const result: Array<FormatedBiShanInfo> = []

  const isPlaybackEnd =
    typeof classhour.studyBuffEndTime === 'number'
      ? now.isAfter(classhour.studyBuffEndTime)
      : typeof classhour.playbackEndTime === 'number'
        ? now.isAfter(classhour.playbackEndTime)
        : true

  result.push({
    type: 'live',
    classhourId: classhour.id,
    isPublish: classhour.publishStatus === 1,
    received: classhour.videoPlay === true,
    isLiveStart: now.isAfter(classhour.startTime),
    isPlaybackNotGenerated: classhour.playbackStatus === 0,
    isLiveEnd: classhour.liveEnd,
    isPlaybackEnd,
    todayLive: now.isAfter(classhour.startTime) && !classhour.liveEnd,
    todayPlayback: classhour.playbackStatus === 1 && !isPlaybackEnd
  })

  classhour.bishanList.forEach(bishan => {
    result.push({
      type: 'homework',
      received: bishan.hasCompleted,
      homeworkTaskId: bishan.homeworkTaskId,
      homeworkTaskName: bishan.name
    } satisfies HomeworkBiShan)
  })

  return result
}
